import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import AddressSearch from "./AddressSearch";
import type { SearchAreaType } from "^/customer/home/useStoreSearch";

export interface AddressInputResult {
  address: string; // 기본 주소(도로명/지번)
  detail: string; // 상세주소(동/호수 등)
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  buildingName?: string;
}

interface Props {
  open: boolean;
  initialBase?: string;
  initialDetail?: string;
  onClose: () => void;
  onComplete: (result: AddressInputResult) => void;
}

/**
 * 주소 검색 → 상세주소 입력 → 확인 을 한 흐름으로 처리하는 모달.
 * (기존 DaumPostcodeModal 은 검색만 하고 닫혀서 상세주소를 받을 곳이 없었음)
 */
export default function AddressInputModal({
  open,
  initialBase = "",
  initialDetail = "",
  onClose,
  onComplete,
}: Props) {
  const [step, setStep] = useState<"search" | "detail">(
    initialBase ? "detail" : "search",
  );
  const [base, setBase] = useState(initialBase);
  const [detail, setDetail] = useState(initialDetail);
  const [extra, setExtra] = useState({
    zonecode: "",
    roadAddress: "",
    jibunAddress: "",
    buildingName: "",
  });

  useEffect(() => {
    if (open) {
      setStep(initialBase ? "detail" : "search");
      setBase(initialBase);
      setDetail(initialDetail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const pick = (selected: SearchAreaType) => {
    const roadAddress = selected.road_address?.address_name ?? "";
    const jibunAddress =
      selected.address?.address_name ?? selected.address_name ?? "";
    const addr = roadAddress || jibunAddress;
    setBase(addr);
    setExtra({
      zonecode: selected.road_address?.zone_no ?? "",
      roadAddress,
      jibunAddress,
      buildingName: selected.road_address?.building_name ?? "",
    });
    setStep("detail");
  };

  const confirm = () => {
    if (!base) {
      setStep("search");
      return;
    }
    // 저장 확정은 onComplete 만 호출 — 닫기는 부모의 onComplete 핸들러가 처리한다.
    // (취소=onClose 와 구분해야 순차 입력 가이드가 다음 항목으로 이어짐)
    onComplete({ address: base, detail: detail.trim(), ...extra });
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-black/45 md:items-center"
      style={{ zIndex: 10000 }}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[480px] flex-col rounded-t-2xl bg-white shadow-2xl md:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#EEE] px-4 py-3">
          <h3 className="text-[15px] font-bold text-[#111]">
            {step === "search" ? "주소 검색" : "주소 입력"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded text-[22px] leading-none text-[#666] hover:bg-[#F5F5F5]"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {step === "search" ? (
          <div className="min-h-[260px] flex-1 overflow-visible px-4 py-4">
            <AddressSearch
              onSelect={pick}
              className="[&_ul]:z-[10001] [&_ul]:max-h-[52vh]"
            />
            <p className="mt-3 text-[12px] leading-relaxed text-[#777]">
              도로명 또는 지번 주소를 입력하고 검색 결과를 선택해 주세요.
            </p>
          </div>
        ) : (
          <div className="px-4 py-4">
            <div className="rounded-xl bg-[#F7F7F8] px-3 py-3">
              <div className="text-[11px] font-medium text-[#999]">선택한 주소</div>
              <div className="mt-0.5 text-[14px] font-semibold text-[#111]">
                {base}
              </div>
              <button
                type="button"
                onClick={() => setStep("search")}
                className="mt-1 text-[12px] font-semibold text-[#FF9934] underline"
              >
                다시 검색
              </button>
            </div>

            <label className="mt-4 block text-[12px] font-semibold text-[#555]">
              상세주소
            </label>
            <input
              autoFocus
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="동/호수 등 상세주소를 입력해 주세요"
              className="mt-1.5 w-full rounded-xl border border-[#E5E5E5] px-3 py-3 text-[14px] focus:border-[#FF9934] focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter") confirm();
              }}
            />

            <button
              type="button"
              onClick={confirm}
              className="mt-4 w-full rounded-2xl bg-[#FF9934] py-3.5 text-[14px] font-bold text-white"
            >
              확인
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
